import { createContext, useContext, useEffect, useState } from 'react'
import { io } from 'socket.io-client'
import { useAuth } from './AuthContext'
import { SOCKET_URLS } from '../config/api.js'
import { getToken, SESSION_EXPIRED_EVENT } from '../utils/session'

const SocketContext = createContext()

export function useSocket() {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

/**
 * The realtime Socket.IO connection (backend namespace /realtime: AI Inspect voice sessions and the
 * professional cards the assistant finds). The server accepts customer tokens only, so it is opened
 * once a customer is signed in and closed when they sign out.
 * (Job, quote and message updates reach customers on WhatsApp; the app reads them from the API.)
 */
export function SocketProvider({ children }) {
  const [realtimeSocket, setRealtimeSocket] = useState(null)
  const { user } = useAuth()

  useEffect(() => {
    const token = getToken()
    if (!user || !token) return

    const socket = io(SOCKET_URLS.REALTIME, {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 5000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      reconnectionDelayMax: 5000
    })

    socket.on('connect', () => console.log('🎤 Realtime socket connected'))
    socket.on('disconnect', () => console.log('🎤 Realtime socket disconnected'))
    socket.on('error', (error) => console.error('🎤 Realtime socket error:', error))
    // The server refuses a missing, expired or non-customer token: the session is over
    socket.on('connect_error', (error) => {
      if (error?.message === 'unauthorized') {
        socket.close()
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
      }
    })

    setRealtimeSocket(socket)
    return () => {
      socket.close()
      setRealtimeSocket(null)
    }
  }, [user?.id])

  return (
    <SocketContext.Provider value={{ realtimeSocket }}>
      {children}
    </SocketContext.Provider>
  )
}
